// Optimization utility functions

import { validateAllInputs } from './validationUtils';
import { API_URL } from '../config';
import { autoSaveOptimizationResults } from './projectUtils';

/**
 * Handle optimization run
 * @param {Array} panels - Array of panel objects
 * @param {Array} stockSheets - Array of stock sheet objects
 * @param {string} kerfThickness - Kerf thickness value
 * @param {boolean} differentMaterials - Whether different materials are enabled
 * @param {boolean} labelsOnPanels - Whether labels on panels are enabled
 * @param {boolean} grainDirection - Whether grain direction is enabled
 * @param {string} unitSystem - Unit system ('imperial', 'metric', 'meters', or 'millimeters')
 * @param {Object} user - Current user object (or null)
 * @param {Function} setOptimizationLoading - State setter for optimization loading
 * @param {Function} setOptimizationResults - State setter for optimization results
 * @param {Function} setUpgradeModalOpen - State setter for upgrade modal
 * @param {Object} db - Firestore database instance (optional, for auto-save)
 * @param {string} currentProjectId - Current project ID (optional, for auto-save)
 */
export const handleRun = async (
  panels,
  stockSheets,
  kerfThickness,
  differentMaterials,
  labelsOnPanels,
  grainDirection,
  unitSystem,
  user,
  setOptimizationLoading,
  setOptimizationResults,
  setUpgradeModalOpen,
  db = null,
  currentProjectId = null
) => {
  setOptimizationLoading(true); // Start loading
  try {
    // Check calculation limit for authenticated users
    if (user) {
      const token = await user.getIdToken();
      const limitCheck = await fetch(`${API_URL}/api/subscription/check-calculation-limit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!limitCheck.ok) {
        console.error('Failed to check calculation limit');
      } else {
        const limitData = await limitCheck.json();
        
        if (!limitData.allowed) {
          alert(limitData.message || 'Daily calculation limit reached. Please upgrade to Premium for unlimited calculations.');
          setUpgradeModalOpen(true);
          setOptimizationLoading(false); // Stop loading
          return;
        }
        
        // Log remaining calculations for free users
        if (limitData.tier === 'free') {
          console.log(`✅ Calculation ${limitData.count}/5 - ${limitData.remaining} remaining today`);
          if (limitData.remaining <= 1) {
            console.warn('⚠️ Low calculation count! Consider upgrading to Premium.');
          }
        }
      }
    }

    // Defensive validation check (button should already be disabled, but check anyway)
    const validation = validateAllInputs(panels, stockSheets, kerfThickness, differentMaterials);
    if (!validation.isValid) {
      const errorMessage = validation.errors.summary || 
        'Please fix all validation errors before running optimization.';
      alert(errorMessage);
      setOptimizationLoading(false);
      return;
    }

    // Filter enabled panels and stock sheets
    // Add original row index before filtering so we can map back later
    const panelsWithIndices = panels.map((p, idx) => ({ ...p, _originalRowIndex: idx }));
    let enabledPanels = panelsWithIndices.filter(p => p.enabled && p.width && p.length && p.quantity);
    let enabledStock = stockSheets.filter(s => s.enabled && s.width && s.length && s.quantity);

    // Generate sequential IDs for all panel instances (1 to n)
    // Each panel row gets an array of IDs based on its quantity
    let nextId = 1;
    enabledPanels = enabledPanels.map((p) => {
      const quantity = parseInt(p.quantity) || 1;
      const ids = [];
      for (let i = 0; i < quantity; i++) {
        ids.push(nextId++);
      }
      return { ...p, ids };
    });

    // Clean up data based on settings
    if (!differentMaterials) {
      enabledPanels = enabledPanels.map(p => ({ ...p, materialType: '' }));
      enabledStock = enabledStock.map(s => ({ ...s, materialType: '' }));
    }
    if (!labelsOnPanels) {
      enabledPanels = enabledPanels.map(p => ({ ...p, label: '' }));
    }
    if (!grainDirection) {
      enabledPanels = enabledPanels.map(p => ({ ...p, grainDirection: 'either' }));
      enabledStock = enabledStock.map(s => ({ ...s, grainDirection: 'either' }));
    }

    // Prepare the request payload
    const requestPayload = {
      panels: enabledPanels,
      stockSheets: enabledStock,
      settings: {
        kerfThickness,
        grainDirection,
        labelsOnPanels,
        differentMaterials,
        unitSystem,
      },
    };

    // DEBUG: Log the complete request JSON
    console.log('📤 Request JSON:', JSON.stringify(requestPayload, null, 2));

    // Retry logic for service spin-up (Render free tier can take up to 50s to spin up)
    // Retry every 5 seconds for 2 minutes (120 seconds / 5 = 24 retries)
    const maxRetries = 24;
    const retryDelay = 5000; // 5 seconds between retries
    let lastError = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Call the optimization API
        const response = await fetch(`${API_URL}/api/optimize`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestPayload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          const status = response.status;
          
          // Retry on 503 (Service Unavailable) - service might be spinning up
          if (status === 503 && attempt < maxRetries) {
            console.log(`⏳ Service unavailable (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${retryDelay/1000}s... (Render free tier can take up to 50s to spin up)`);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            lastError = errorData;
            continue; // Retry
          }
          
          // For other errors or final attempt, throw immediately
          console.error('❌ Server error:', errorData);
          throw new Error(errorData.error || errorData.message || 'Optimization failed');
        }

        // Success - break out of retry loop
        const result = await response.json();
        if (attempt > 0) {
          console.log(`✅ Optimization succeeded after ${attempt + 1} attempt(s)`);
        }
        
        // DEBUG: Log the complete response JSON
        console.log('📥 Response JSON:', JSON.stringify(result, null, 2));
        
        // Store results to display below (new structure)
        // Include the enabledPanels array with IDs for label syncing
        setOptimizationResults({
          ...result,
          _panelIdMapping: enabledPanels // Store the panel-to-ID mapping for label sync
        });
        
        // Auto-save optimization results to current project if user is logged in
        if (db && user && currentProjectId) {
          await autoSaveOptimizationResults(
            db,
            user,
            currentProjectId,
            panels,
            stockSheets,
            kerfThickness,
            labelsOnPanels,
            differentMaterials,
            grainDirection,
            unitSystem,
            result
          );
        }
        return; // Success - exit function
      } catch (error) {
        // If it's a network error and we have retries left, continue
        if (attempt < maxRetries && (error.message.includes('fetch') || error.message.includes('network'))) {
          console.log(`⏳ Network error (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${retryDelay/1000}s...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          lastError = error;
          continue;
        }
        
        // Re-throw if no retries left or non-retryable error
        throw error;
      }
    }

    // If we exhausted all retries, throw the last error
    if (lastError) {
      throw new Error(lastError.error || lastError.message || 'Optimization failed after multiple attempts');
    }
  } catch (error) {
    console.error('Optimization error:', error);
    alert(`Optimization failed: ${error.message}\nPlease check the console for details.`);
  } finally {
    setOptimizationLoading(false); // Stop loading
  }
};

